import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TRAINING_TYPE_OPTIONS, trainingApi } from '@/entities/Training';
import type {
  TrainingListFilters,
  TrainingListInstructorFilter,
  TrainingListSort,
  TrainingListOrder,
  TrainingStatus,
  TrainingType,
} from '@/entities/Training';
import { QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
import type { OrderOptions } from '@/shared/lib';
import { INFINITE_SCROLL_PAGE_SIZE, usePaginatedListFetch } from './usePaginatedListFetch';

export const TRAINING_LIST_QUANTITY_OPTIONS = QUANTITY_OPTIONS.map((option) =>
  option.id === SHOW_ALL_ITEMS_QUANTITY ? { ...option, name: 'Todos os treinamentos' } : option
);

export const TRAINING_LIST_ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Por mais recente', sort: 'createdAt', order: 'desc' },
  { label: 'Por mais antigo', sort: 'createdAt', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'title', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'title', order: 'desc' },
];

const DEFAULT_LIMIT = 10;
const LIMIT_OPTIONS = new Set(TRAINING_LIST_QUANTITY_OPTIONS.map((option) => option.id));
const SORT_FIELDS = new Set<string>(['createdAt', 'title']);
const SORT_ORDERS = new Set<string>(['asc', 'desc']);
const INSTRUCTOR_FILTER_VALUES = new Set<string>(['INTERNAL', 'EXTERNAL']);
const STATUS_FILTER_VALUES = new Set<string>(['PENDING', 'COMPLETED']);
const TRAINING_TYPE_VALUES = new Set<string>(TRAINING_TYPE_OPTIONS.map((option) => option.value));
const CIVIL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const ERROR_MESSAGE = 'Não foi possível carregar os treinamentos. Tente novamente.';

function getQueryString(value: unknown): string {
  if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : '';
  return typeof value === 'string' ? value : '';
}

function parsePositiveInteger(value: unknown): number | undefined {
  const parsed = Number(getQueryString(value));
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
}

function parseEnumValue<T extends string>(value: unknown, allowed: Set<string>): T | undefined {
  const parsed = getQueryString(value);
  return allowed.has(parsed) ? (parsed as T) : undefined;
}

function parseBooleanQuery(value: unknown): boolean | undefined {
  const raw = getQueryString(value);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
}

function parseCivilDateQuery(value: unknown): string | undefined {
  const parsed = getQueryString(value);
  return CIVIL_DATE_PATTERN.test(parsed) ? parsed : undefined;
}

function cleanFilters(filters: TrainingListFilters): TrainingListFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as TrainingListFilters;
}

export function useTrainingListSearch() {
  const route = useRoute();
  const router = useRouter();

  const page = ref(1);
  const limit = ref(DEFAULT_LIMIT);
  const term = ref('');
  const sort = ref<TrainingListSort | ''>('');
  const order = ref<TrainingListOrder | ''>('');
  const filters = ref<TrainingListFilters>({});
  const hasHydratedFromUrl = ref(false);

  const normalizedTerm = computed(() => term.value.trim());
  const isInfiniteScrollMode = computed(() => limit.value === SHOW_ALL_ITEMS_QUANTITY);
  const hasActiveFilters = computed(() => Object.keys(filters.value).length > 0);
  const hasActiveSearch = computed(() => normalizedTerm.value !== '' || hasActiveFilters.value);

  function hydrateFromQuery() {
    const query = route.query;

    const requestedLimit = Number(query.itemsPerPage);
    limit.value = LIMIT_OPTIONS.has(requestedLimit) ? requestedLimit : DEFAULT_LIMIT;

    page.value = isInfiniteScrollMode.value ? 1 : Math.max(1, Number(query.page) || 1);

    term.value = typeof query.search === 'string' ? query.search : '';

    const requestedSort = query.sort;
    sort.value = SORT_FIELDS.has(requestedSort as string)
      ? (requestedSort as TrainingListSort)
      : '';

    const requestedOrder = query.order;
    order.value = SORT_ORDERS.has(requestedOrder as string)
      ? (requestedOrder as TrainingListOrder)
      : '';

    if (!sort.value || !order.value) {
      sort.value = '';
      order.value = '';
    }

    filters.value = cleanFilters({
      type: parseEnumValue<TrainingType>(query.type, TRAINING_TYPE_VALUES),
      instructor: parseEnumValue<TrainingListInstructorFilter>(
        query.instructor,
        INSTRUCTOR_FILTER_VALUES
      ),
      certificateRequired: parseBooleanQuery(query.certificateRequired),
      status: parseEnumValue<TrainingStatus>(query.status, STATUS_FILTER_VALUES),
      memberId: parsePositiveInteger(query.memberId),
      positionId: parsePositiveInteger(query.positionId),
      trainingDate: parseCivilDateQuery(query.trainingDate),
    });

    hasHydratedFromUrl.value = true;
  }

  function buildQueryFromState(): Record<string, string> {
    const query: Record<string, string> = {};
    if (page.value > 1) query.page = String(page.value);
    if (limit.value !== DEFAULT_LIMIT) query.itemsPerPage = String(limit.value);
    if (normalizedTerm.value) query.search = normalizedTerm.value;
    if (sort.value) query.sort = sort.value;
    if (order.value) query.order = order.value;
    for (const [key, value] of Object.entries(filters.value)) {
      query[key] = String(value);
    }
    return query;
  }

  function syncUrl() {
    if (!hasHydratedFromUrl.value) return;
    router.replace({ path: route.path, query: buildQueryFromState() });
  }

  hydrateFromQuery();

  function requestPage(targetPage: number) {
    return trainingApi.listTrainings({
      page: targetPage,
      limit: isInfiniteScrollMode.value ? INFINITE_SCROLL_PAGE_SIZE : limit.value,
      term: normalizedTerm.value || undefined,
      sort: sort.value || undefined,
      order: order.value || undefined,
      ...filters.value,
    });
  }

  const {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    error,
    isInitialLoading,
    fetch,
    loadMore,
  } = usePaginatedListFetch({
    page,
    isInfiniteScrollMode,
    errorMessage: ERROR_MESSAGE,
    requestPage,
  });

  const hasItems = computed(() => items.value.length > 0);
  const showControls = computed(() => hasItems.value || hasActiveSearch.value);

  function changePage(newPage: number) {
    page.value = newPage;
  }

  function changeLimit(newLimit: number) {
    limit.value = newLimit;
    page.value = 1;
  }

  function changeSearch(newTerm: string) {
    if (newTerm.trim() === normalizedTerm.value) {
      term.value = newTerm;
      return;
    }
    term.value = newTerm;
    page.value = 1;
  }

  function changeSort(newSort: string, newOrder: string) {
    if (newSort === '' && newOrder === '') {
      sort.value = '';
      order.value = '';
      page.value = 1;
      return;
    }

    if (!SORT_FIELDS.has(newSort) || !SORT_ORDERS.has(newOrder)) return;

    sort.value = newSort as TrainingListSort;
    order.value = newOrder as TrainingListOrder;
    page.value = 1;
  }

  function applyFilters(newFilters: TrainingListFilters) {
    filters.value = cleanFilters(newFilters);
    page.value = 1;
  }

  function clearFilters() {
    filters.value = {};
    page.value = 1;
  }

  watch(
    [page, limit, normalizedTerm, sort, order, filters],
    () => {
      syncUrl();
      fetch();
    },
    { immediate: true, deep: true }
  );

  return {
    items,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    isInitialLoading,
    isInfiniteScrollMode,
    error,
    page,
    limit,
    term,
    sort,
    order,
    filters,
    hasActiveSearch,
    hasActiveFilters,
    showControls,
    changePage,
    changeLimit,
    changeSearch,
    changeSort,
    applyFilters,
    clearFilters,
    loadMore,
    fetch,
  };
}
